import { makeAutoObservable } from 'mobx'

import authApi from '@/api/auth.api'
import type { AuthRoute } from '@/router'
import { authService } from '@/service/auth.service'
import { CryptoUtils } from '@/utils'

export interface EncryptedLoginParams {
  id: string
  pw: string
  ivId?: string
  ivPw?: string
  key?: string
}

export interface DecryptedLoginData {
  id: string
  password: string
}

export interface AESEncryptedData {
  id: string
  pw: string
  key: string
  iv: string
}

export default class GuestLoginService {
  constructor() {
    makeAutoObservable(this)
  }

  /**
   * 암호화된 로그인 정보를 복호화합니다.
   */
  private async _decryptLoginData(params: EncryptedLoginParams): Promise<DecryptedLoginData> {
    const { id: encryptedId, pw: encryptedPw, ivId, ivPw, key } = params

    if (!encryptedId || !encryptedPw || !key || !ivId || !ivPw) {
      throw new Error('암호화된 로그인 정보가 없습니다.')
    }

    // AES 복호화 방식 (key, iv가 제공된 경우)
    const decryptedIdResult = await CryptoUtils.decryptAES(encryptedId, key, ivId)
    const decryptedPwResult = await CryptoUtils.decryptAES(encryptedPw, key, ivPw)

    return {
      id: decryptedIdResult,
      password: decryptedPwResult,
    }
  }

  /**
   * 복호화된 로그인 정보로 자동 로그인을 수행합니다.
   */
  async performAutoLogin(loginData: DecryptedLoginData) {
    try {
      // 로그인 API 호출
      const { data: loginResponse } = await authApi.postLogin({
        id: loginData.id.trim(),
        password: loginData.password.trim(),
        userType: 'TEACHER',
        serviceType: 'MATHFLAT',
      })

      // 로그인 성공 처리
      if (loginResponse) {
        await authService.finalizeJwtLogin(loginResponse)

        authService.saveLoginResponseToStorage(loginResponse, window.location.pathname as AuthRoute)

        return loginResponse
      }

      throw new Error('로그인 결과가 없습니다.')
    } catch (loginError) {
      console.error('자동 로그인 실패:', loginError)

      throw loginError
    }
  }

  /**
   * URL에서 암호화된 파라미터를 제거합니다.
   */
  clearEncryptedParams() {
    const url = new URL(window.location.href)
    url.searchParams.delete('id')
    url.searchParams.delete('pw')
    url.searchParams.delete('key')
    url.searchParams.delete('ivId')
    url.searchParams.delete('ivPw')
    window.history.replaceState(null, '', url.toString())
  }

  /**
   * 암호화된 로그인 전체 프로세스를 처리합니다.
   */
  async handleEncryptedLogin(params: EncryptedLoginParams) {
    try {
      // 1. 암호화된 데이터 복호화
      const decryptedData = await this._decryptLoginData(params)

      // 2. 자동 로그인 수행
      const loginResult = await this.performAutoLogin(decryptedData)

      return loginResult
    } finally {
      // 3. URL에서 암호화된 파라미터 제거
      this.clearEncryptedParams()
    }
  }

  /**
   * 테스트용 암호화 데이터를 생성합니다.
   */
  async generateTestEncryptedData(id: string, password: string, key?: string) {
    try {
      // 공통 키 생성 (ID와 PW를 같은 키로 암호화)
      const commonKey = key || CryptoUtils.generateSalt(32)

      // ID 암호화
      const idResult = await CryptoUtils.encryptAES(id, commonKey)

      // PW 암호화 (같은 키 사용)
      const pwResult = await CryptoUtils.encryptAES(password, commonKey)

      return {
        id: idResult.encrypted,
        pw: pwResult.encrypted,
        key: commonKey,
        ivId: idResult.iv,
        ivPw: pwResult.iv,
      }
    } catch (error) {
      console.error('AES 암호화 데이터 생성 실패:', error)
      throw error
    }
  }
}

export const guestLoginService = new GuestLoginService()
